import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { getSocket } from '../../../shared/socket.js';

function safeGet(path, params) {
  return api
    .get(path, { params })
    .then((r) => r.data)
    .catch((err) => {
      // لا نبتلع الخطأ بصمت: بطاقة فارغة بلا سبب كانت أسوأ عطل في اللوحة.
      const status = err?.response?.status || '?';
      const msg = err?.response?.data?.error?.message || err?.message || 'unknown';
      // eslint-disable-next-line no-console
      console.warn(`[dashboard] ${path} failed:`, status, msg);
      return null;
    });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function useDashboard({ from, to } = {}) {
  const qc = useQueryClient();

  const overview = useQuery({
    queryKey: ['dash', 'overview', from, to],
    queryFn: () => safeGet('/reports/overview', { from, to }),
    staleTime: 30_000
  });

  // الإيراد بالساعة على كامل المدى المختار لا على يومه الأخير فقط:
  // اختيار «الأسبوع»/«الشهر» كان يُرسل `date` واحداً فيظهر الرسم فارغاً.
  const hourly = useQuery({
    queryKey: ['dash', 'hourly', from || '', to || todayISO()],
    queryFn: () => safeGet('/reports/revenue/by-hour', { from: from || todayISO(), to: to || todayISO() }),
    staleTime: 30_000
  });

  const donut = useQuery({
    queryKey: ['dash', 'donut', from, to],
    queryFn: () => safeGet('/reports/revenue/by-category', { from, to }),
    staleTime: 30_000
  });

  const activeSessions = useQuery({
    queryKey: ['dash', 'active-sessions'],
    queryFn: () => safeGet('/sessions/active'),
    staleTime: 15_000
  });

  // الفواتير المعلّقة من مسار التقارير لا من /pos/sales:
  //   • /pos/sales يعيد مستندات خامة (grandTotal/dueAmount و childId مُعبّأ
  //     ككائن) بينما اللوحة تقرأ totalAmount/childName، فكانت كل القيم صفراً
  //     والأسماء «—».
  //   • وهو مقيّد بصفحة واحدة بحد 50، ويقتصر على مدى اليوم في بقية الشاشات.
  // مسار التقارير يعيد بالضبط ما تعرضه اللوحة، وبلا مدى: الفاتورة المعلّقة
  // حالة قائمة الآن مهما كان تاريخها.
  const pendingInvoices = useQuery({
    queryKey: ['dash', 'pending-invoices'],
    queryFn: () => safeGet('/reports/pending-invoices', { limit: 100 }),
    staleTime: 15_000
  });

  const latestInvoices = useQuery({
    queryKey: ['dash', 'latest-invoices'],
    queryFn: () => safeGet('/pos/sales', { limit: 8, sort: '-createdAt' }),
    staleTime: 30_000
  });

  const lowStock = useQuery({
    queryKey: ['dash', 'low-stock'],
    queryFn: async () => {
      const r = await safeGet('/reports/low-stock');
      if (r) return r;
      return safeGet('/catalog/products', { lowStock: true, limit: 10 });
    },
    staleTime: 60_000
  });

  const topSpenders = useQuery({
    queryKey: ['dash', 'top-spenders', from, to],
    queryFn: () => safeGet('/reports/top-spenders', { from, to, limit: 5 }),
    staleTime: 60_000
  });

  useEffect(() => {
    let pos;
    let child;
    // كل حدث يغيّر حالة فاتورة يجب أن يحدّث قائمة المعلّقة: الإنشاء يضيف
    // فاتورة معلّقة، والدفع/التسوية يُخرجها، والإلغاء يُخرجها كذلك.
    // الاكتفاء بـ invoice_created كان يترك القائمة قديمة بعد كل تحصيل.
    const invalidateAll = () => qc.invalidateQueries({ queryKey: ['dash'] });
    const posEvents = ['invoice_created', 'invoice_paid', 'invoice_cancelled', 'session_settled'];
    try {
      pos = getSocket('/pos');
      posEvents.forEach((ev) => pos.on(ev, invalidateAll));
    } catch { /* socket optional */ }
    try {
      child = getSocket('/');
      child.on('child:active', () => {
        qc.invalidateQueries({ queryKey: ['dash', 'active-sessions'] });
        qc.invalidateQueries({ queryKey: ['dash', 'overview'] });
        qc.invalidateQueries({ queryKey: ['dash', 'pending-invoices'] });
      });
    } catch { /* socket optional */ }
    return () => {
      try { posEvents.forEach((ev) => pos?.off(ev, invalidateAll)); } catch { /* */ }
      try { child?.off('child:active'); } catch { /* */ }
    };
  }, [qc]);

  return {
    overview,
    hourly,
    donut,
    activeSessions,
    pendingInvoices,
    latestInvoices,
    lowStock,
    topSpenders
  };
}
